import React, { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';

import { Input } from '../Input';
import { EditorModal } from '..';

type EditableInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  multiline?: boolean;
  label?: string;
  disabled?: boolean;
  readonly?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
  iconPosition?: 'left' | 'top';
  mode?: 'edit' | 'library';
  onSavePrompt?: (content: string) => void;
};

export const EditableInputModal: React.FC<EditableInputProps> = ({
  value = '',
  onChange,
  disabled,
  readonly = false,
  multiline = false,
  onEditingChange,
  iconPosition = 'left',
  mode = 'library',
  onSavePrompt,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editorContent, setEditorContent] = useState<string>(value);
  const isLibraryMode = mode === 'library';

  const handleEditingChange = (editing: boolean) => {
    setIsEditing(editing);
    onEditingChange?.(editing);
  };

  const handleChanges = (value: string) => {
    setEditorContent(value);
    onChange?.(value);
  };

  const handleSave = (content: string) => {
    handleChanges(content);
    handleEditingChange(false);
    onSavePrompt?.(content);
  };

  const isTopPosition = iconPosition === 'top';

  return (
    <div className="relative w-full">
      {!disabled && (
        <IconButton
          onClick={() => handleEditingChange(true)}
          className={
            isTopPosition
              ? 'absolute -top-12 left-28 px-3 py-2 text-main border border-[#e5e7eb] border-solid rounded shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2'
              : 'absolute -left-16 top-4 px-3 py-2 text-main border border-[#e5e7eb] border-solid rounded shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2'
          }
        >
          <EditIcon />
        </IconButton>
      )}
      <Input
        value={editorContent}
        onChange={handleChanges}
        disabled={disabled}
        readonly={readonly}
        multiline={multiline}
      />
      {isEditing && (
        <EditorModal
          initialContent={editorContent}
          isLibraryMode={isLibraryMode}
          onClose={() => handleEditingChange(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};
